// embed.js — the loader script clinics paste into their site.
//
//   <div data-clinic-form="f_XXXXXX"></div>
//   <script src="https://forms.coherenthealthcare.com/embed.js" async></script>
//
// Finds each marker div, replaces it with a sandboxed iframe pointed at
// /embed/<publicId>, and resizes the iframe to fit its content via the
// postMessage protocol in src/resize.ts. No React, no deps — keep this
// small (target < 2 KB gzipped).

const SELECTOR = '[data-clinic-form]:not([data-coherent-mounted])'
const IFRAME_SRC_TEMPLATE = '/embed/'  // resolved against the script's own origin
const RESIZE_MESSAGE_TYPE = 'coherent:webform:resize'
const REDIRECT_MESSAGE_TYPE = 'coherent:webform:redirect'

type ResizeMessage = { type: typeof RESIZE_MESSAGE_TYPE; height: number; iframeId: string }
type RedirectMessage = { type: typeof REDIRECT_MESSAGE_TYPE; url: string; iframeId: string }

// Discover the script's own origin so the iframe URL matches. Falls back to
// the configured host on document.currentScript if accessed before parse.
function scriptOrigin(): string {
    const current = document.currentScript as HTMLScriptElement | null
    if (current?.src) {
        try { return new URL(current.src).origin } catch { /* noop */ }
    }
    // Last-resort fallback — same origin as the host page (only useful in
    // local dev where the embed.js is served from the dev server).
    return window.location.origin
}

const ORIGIN = scriptOrigin()
let mountCounter = 0
const iframesById = new Map<string, HTMLIFrameElement>()

function mount(target: HTMLElement): void {
    const publicId = target.getAttribute('data-clinic-form')
    if (!publicId) return

    target.setAttribute('data-coherent-mounted', 'true')

    const iframeId = `coherent-webform-${++mountCounter}`
    const iframe = document.createElement('iframe')
    iframe.id = iframeId
    iframe.src = `${ORIGIN}${IFRAME_SRC_TEMPLATE}?publicId=${encodeURIComponent(publicId)}&iframeId=${iframeId}`
    iframe.title = 'Webform'
    iframe.loading = 'lazy'
    iframe.style.width = '100%'
    iframe.style.minHeight = '480px'
    iframe.style.border = '0'
    iframe.style.display = 'block'
    // `allow-same-origin` is required so the iframe document has the real
    // `forms.coherent…` origin — without it browsers serve postMessage events
    // with `origin === 'null'` and our origin check below drops them. The
    // sandbox still blocks top-navigation and other capabilities by default.
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups')

    target.appendChild(iframe)
    iframesById.set(iframeId, iframe)
}

function isResizeMessage(data: unknown): data is ResizeMessage {
    if (!data || typeof data !== 'object') return false
    const m = data as { type?: unknown; height?: unknown; iframeId?: unknown }
    return m.type === RESIZE_MESSAGE_TYPE
        && typeof m.height === 'number'
        && isFinite(m.height)
        && typeof m.iframeId === 'string'
}

function isRedirectMessage(data: unknown): data is RedirectMessage {
    if (!data || typeof data !== 'object') return false
    const m = data as { type?: unknown; url?: unknown; iframeId?: unknown }
    return m.type === REDIRECT_MESSAGE_TYPE
        && typeof m.url === 'string'
        && typeof m.iframeId === 'string'
}

// Defence-in-depth: the URL is already validated server-side against
// http(s), but the loader runs on the merchant's page — never navigate to
// javascript: / data: / etc even if a message somehow got through.
function isSafeNavigationUrl(url: string): boolean {
    try {
        const parsed = new URL(url, window.location.href)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
        return false
    }
}

window.addEventListener('message', (event) => {
    // Only accept messages from our own iframes (origin must match where we serve them).
    if (event.origin !== ORIGIN) return
    if (isResizeMessage(event.data)) {
        const iframe = iframesById.get(event.data.iframeId)
        if (!iframe) return
        iframe.style.height = `${event.data.height}px`
        return
    }
    if (isRedirectMessage(event.data)) {
        if (!iframesById.has(event.data.iframeId)) return
        if (!isSafeNavigationUrl(event.data.url)) return
        // window.top is typed nullable for detached windows; fall back to
        // self so a stray detached-frame case doesn't crash the loader.
        const top = window.top ?? window
        top.location.href = event.data.url
    }
})

function mountAll(): void {
    const targets = document.querySelectorAll<HTMLElement>(SELECTOR)
    targets.forEach(mount)
}

// Watch for late-added markers (e.g. SPAs that inject a marker after page load).
// Must wait for document.body — if the script is included in <head>, body isn't
// parsed yet and observe() throws.
function startObserver(): void {
    if (typeof MutationObserver === 'undefined' || !document.body) return
    new MutationObserver(() => mountAll()).observe(document.body, {
        childList: true,
        subtree: true,
    })
}

// Mount synchronously for any markers already in the DOM, then watch for new ones.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        mountAll()
        startObserver()
    })
} else {
    mountAll()
    startObserver()
}
